// src/features/analytics/components/ConcurrencyHeatmapCard.tsx

import { Fragment } from 'react';
import { Paper, Skeleton, Text, Title, Tooltip } from '@mantine/core';
import { HEATMAP_MAX_RANGE_DAYS, type UtcRange } from '@/shared/utils/utcRange';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import type { AnalyticsEnvelope, HeatmapCell, HeatmapData } from '../types';

interface ConcurrencyHeatmapCardProps {
  heatmap: AnalyticsEnvelope<HeatmapData> | null;
  /** The CLAMPED range this card actually queried, not the page range. */
  range: UtcRange;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
  /** True when the page clamped the selected range to the 90-day cap. */
  rangeWasClamped: boolean;
}

// Go time.Weekday numbering: 0=Sunday .. 6=Saturday, UTC.
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Brand-blue cell fill scaled by peak concurrency (0 → near-transparent). */
function cellColor(peak: number, maxPeak: number): string {
  if (maxPeak <= 0 || peak <= 0) return 'rgba(59, 130, 246, 0.06)';
  const alpha = 0.15 + 0.85 * (peak / maxPeak);
  return `rgba(59, 130, 246, ${alpha.toFixed(3)})`;
}

/**
 * 7×24 UTC weekday/hour concurrency matrix as a custom CSS grid — no chart
 * dependency added for this (ADR-003 §10).
 */
export function ConcurrencyHeatmapCard({
  heatmap,
  range,
  isLoading,
  isError,
  error,
  onRetry,
  rangeWasClamped,
}: ConcurrencyHeatmapCardProps) {
  const cells = heatmap?.data.cells ?? [];
  const byKey = new Map<number, HeatmapCell>();
  let maxPeak = 0;
  for (const cell of cells) {
    byKey.set(cell.weekday * 24 + cell.hour, cell);
    if (cell.peakConcurrent > maxPeak) maxPeak = cell.peakConcurrent;
  }

  return (
    <Paper withBorder p="md">
      <Title order={5} mb={4}>
        Concurrency heatmap (UTC)
      </Title>
      {rangeWasClamped && (
        <Text size="xs" c="dimmed" mb="xs">
          The heatmap is limited to the most recent {HEATMAP_MAX_RANGE_DAYS} days of the
          selected range.
        </Text>
      )}

      {isLoading ? (
        <Skeleton height={200} />
      ) : isError ? (
        <AnalyticsErrorState error={error} onRetry={onRetry} />
      ) : cells.length === 0 ? (
        <AnalyticsCoverageNotice
          coverage={heatmap?.coverage}
          freshness={heatmap?.freshness}
          range={range}
          emptyMessage="No concurrency data for this range."
        />
      ) : (
        <>
          <AnalyticsCoverageNotice
            coverage={heatmap?.coverage}
            freshness={heatmap?.freshness}
            range={range}
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '44px repeat(24, 1fr)',
              gap: 2,
              marginTop: 8,
            }}
          >
            <div />
            {Array.from({ length: 24 }, (_, hour) => (
              <Text key={`h${hour}`} size="xs" c="dimmed" ta="center">
                {hour % 3 === 0 ? hour : ''}
              </Text>
            ))}
            {WEEKDAY_LABELS.map((label, weekday) => (
              <Fragment key={label}>
                <Text size="xs" c="dimmed" pr={4} ta="right">
                  {label}
                </Text>
                {Array.from({ length: 24 }, (_, hour) => {
                  const cell = byKey.get(weekday * 24 + hour);
                  const tooltip = cell
                    ? `${label} ${hour}:00 UTC — peak ${AnalyticsUtils.formatCount(cell.peakConcurrent)}, avg ${cell.avgConcurrent.toFixed(1)}, samples ${cell.sampleCount}`
                    : `${label} ${hour}:00 UTC — no samples`;
                  return (
                    <Tooltip key={`c${weekday}-${hour}`} label={tooltip} withArrow>
                      <div
                        style={{
                          aspectRatio: '1.4',
                          minHeight: 14,
                          borderRadius: 2,
                          background: cellColor(cell?.peakConcurrent ?? 0, maxPeak),
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Fragment>
            ))}
          </div>
          <Text size="xs" c="dimmed" mt={6}>
            Hours are UTC. Cell intensity shows peak concurrent viewers.
          </Text>
        </>
      )}
    </Paper>
  );
}
